// Web Push. On iPhone this only works when the app is installed to the Home Screen
// (iOS 16.4+) and permission is requested from a tap.

import { config } from '../config.ts';
import type { PersonId } from '@shared/types.ts';
import type { SupabaseSource } from '../data/supabaseSource.ts';

export function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export type PushState = 'unsupported' | 'needs-install' | 'not-configured' | 'default' | 'granted' | 'denied';

export function pushState(): PushState {
  if (!pushSupported()) return isIos() && !isStandalone() ? 'needs-install' : 'unsupported';
  if (isIos() && !isStandalone()) return 'needs-install';
  if (!config.vapidPublicKey) return 'not-configured';
  return Notification.permission as PushState;
}

export function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Ask permission (must run inside a tap), subscribe, and store the subscription. */
export async function enablePush(source: SupabaseSource, me: PersonId | undefined): Promise<string> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'Notifications were not allowed.';
  const reg = await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(config.vapidPublicKey) }));
  const json = sub.toJSON();
  const { error } = await source.db.from('push_subscriptions').upsert({
    id: json.endpoint,
    person_id: me ?? null,
    endpoint: json.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth,
    user_agent: navigator.userAgent,
  });
  if (error) return `Couldn't save the subscription: ${error.message}`;
  return 'Notifications are on for this device.';
}
